// @flow strict

import React, { useEffect, useRef, useContext } from 'react';
import { requestTTICallback } from '@ta/platform.tti';
import { Environment, Device, Visitor } from '@ta/lithium.context';
// flowlint untyped-import:off
import lifecycle from './page-lifecycle';
// flowlint untyped-import:error

type RumPayload = {|
  +checksum: number,
  +pageLoad: {|
    +uid: string,
    +page: string,
    +locale: string,
    +revision: string,
    +is_logged_in: boolean,
    +viewport_category: string,
    +is_webview: boolean,
    user_agent: string,
    connection_type: string,
  |},
  timings: [string, string, number, number][],
  metrics: { [string]: number, ... },

  // This is here to retain backward compatibility with old dashboards & ETLs
  navigations: {|
    +puid: string,
    name: string,
    dns: number,
    ttfb: number,
    download: number,
    size: number,
  |}[],
|};

// https://wicg.github.io/event-timing/
declare class PerformanceEventTiming extends PerformanceEntry {
  +processingStart: DOMHighResTimeStamp;
  +processingEnd: DOMHighResTimeStamp;
  +cancelable: boolean;
}

// https://www.w3.org/TR/resource-timing-2/
interface PerformanceResourceTiming extends PerformanceEntry {
  +initiatorType: string;
  +redirectStart: DOMHighResTimeStamp;
  +redirectEnd: DOMHighResTimeStamp;
  +fetchStart: DOMHighResTimeStamp;
  +domainLookupStart: DOMHighResTimeStamp;
  +domainLookupEnd: DOMHighResTimeStamp;
  +connectStart: DOMHighResTimeStamp;
  +connectEnd: DOMHighResTimeStamp;
  +secureConnectionStart: DOMHighResTimeStamp;
  +requestStart: DOMHighResTimeStamp;
  +responseStart: DOMHighResTimeStamp;
  +responseEnd: DOMHighResTimeStamp;
  +transferSize: number;
  +encodedBodySize: number;
  +decodedBodySize: number;
}

// https://wicg.github.io/layout-instability/
interface LayoutShift extends PerformanceEntry {
  +hadRecentInput: boolean;
  +value: number;
  +lastInputTime: DOMHighResTimeStamp;
}

// https://wicg.github.io/element-timing/
interface PerformanceElementTiming extends PerformanceEntry {
  +renderTime: DOMHighResTimeStamp;
  +loadTime: DOMHighResTimeStamp;
  +intersectionRect: DOMRectReadOnly;
  +identifier: string;
  +naturalWidth: number;
  +naturalHeight: number;
  +id: string;
  +element: ?Element;
  +url: string;
}

// https://wicg.github.io/largest-contentful-paint/
interface LargestContentfulPaint extends PerformanceEntry {
  +renderTime: DOMHighResTimeStamp;
  +loadTime: DOMHighResTimeStamp;
  +size: number;
  +id: string;
  +element: ?Element;
  +url: string;
}

// https://w3c.github.io/longtasks/
interface TaskAttributionTiming extends PerformanceEntry {
  +containerType: string;
  +containerSrc: string;
  +containerId: string;
  +containerName: string;
}

// https://w3c.github.io/longtasks/
interface PerformanceLongTaskTiming extends PerformanceEntry {
  +attribution: $ReadOnlyArray<TaskAttributionTiming>;
}

// https://www.w3.org/TR/user-timing-3/
type PerformanceMarkOptions = {|
  +detail?: mixed,
  +startTime?: number,
|};

// Ponyfill of https://www.w3.org/TR/user-timing-3/#the-performancemark-constructor
const mark = (name, options: PerformanceMarkOptions) =>
  Object.create(PerformanceEntry.prototype, {
    name: { value: name },
    entryType: { value: 'mark' },
    startTime: {
      value: (() => {
        if (typeof options.startTime !== 'undefined') {
          if (options.startTime < 0) {
            throw new TypeError('Invalid startTime');
          }
          return options.startTime;
        }
        return performance.now();
      })(),
    },
    duration: { value: 0 },
    detail: { value: options.detail },
  });

const MARK_WHITELIST = new Set([
  'time-to-interactive',
  'first-paint',
  'first-contentful-paint',
  'cpm-hero-latency',
  'first-input-delay',
  'load',
  'time-to-first-byte',
  'time-to-last-byte',
  'request-start',
  'fetch-start',
]);

const safeAdd = (obj: { [string]: number, ... }, property: string, value: number) => {
  // We need to accumulate metrics freqently in this module. This is the fastest way of doing that.
  /* eslint-disable no-param-reassign */
  if (property in obj) {
    obj[property] += value;
  } else {
    obj[property] = value;
  }
  /* eslint-enable no-param-reassign */
};

const sum = (a, i) => a + i;
const encoder = new TextEncoder();
const threeDigits = n => Math.round(n * 10e3) / 10e3;

// Historically we screwed up placement configs and recorded rum metrics multiple times for the same
// page view. To prevent this on a deep level we do a bad thing and make RUM itself a singleton.
let tracked = false;

const useRUM = (page: string, uid: string) => {
  const { domainName, locale, version, cdnPrefix } = useContext(Environment);
  const { userId } = useContext(Visitor);
  const { viewportCategory, isWebview } = useContext(Device);

  const haveReachedTTI = useRef<boolean>(false);
  const metricsAreRepresentative = useRef<boolean>(true);
  const payload = useRef<RumPayload>({
    // We use this checksum to rule out naive fuzzing attempts on the server.
    checksum: [uid, page].map(str => Array.from(encoder.encode(str)).reduce(sum, 0)).reduce(sum, 0),
    pageLoad: {
      uid,
      page,
      locale: locale.code,
      revision: version,
      is_logged_in: userId !== null,
      viewport_category: viewportCategory,
      is_webview: isWebview,
      user_agent: 'unknown',
      connection_type: 'unknown',
    },
    timings: [],
    metrics: {},
    navigations: [],
  });

  useEffect(() => {
    if (
      // Browser support check. sendBeacon is polyfilled, so no need to worry about that.
      // PerformanceObserver limits us to: Chrome 52, Safari 11, Firefox 57
      typeof PerformanceObserver === 'undefined'
    ) {
      return undefined;
    }

    // It's possible for a race condition between PerformanceObserver buffering and getEntries to
    // result in duplicated performance entries being tracked. This eliminates that duplication.
    // It's only needed as long as we're not using PerformanceObserver with the buffered flag.
    const trackedEntries = new Set();

    // If we're re-initializing this effect, then whatever data is in the payload is bad.
    payload.current.metrics = {};
    payload.current.timings = [];
    payload.current.navigations = [];

    const handlePerformanceEntry = (entries: PerformanceEntry[]) => {
      entries
        .filter(entry => entry.entryType !== 'mark' || MARK_WHITELIST.has(entry.name))
        // Filter out React Dev Tools 'measure's
        .filter(
          entry =>
            entry.entryType !== 'measure' ||
            !// React starts each of its measure entries with either the atom symbol:
            (
              entry.name.startsWith('\u269B') ||
              // Or the "do not enter" symbol:
              entry.name.startsWith('\u26D4')
            ),
        )
        .filter(entry => !trackedEntries.has(entry))
        .forEach(entry => {
          trackedEntries.add(entry);
          payload.current.timings.push([
            entry.name,
            entry.entryType,
            Math.round(entry.startTime),
            Math.round(entry.duration),
          ]);
        });
    };

    const handleNavigationTiming = (entries: PerformanceNavigationTiming[]) => {
      if (entries.length > 0) {
        // If there are multiple entries, they're redirects, so we only care about the last one.
        const entry = entries[entries.length - 1];
        if (trackedEntries.has(entry)) {
          return;
        }
        trackedEntries.add(entry);

        try {
          handlePerformanceEntry([
            mark('fetch-start', { startTime: entry.fetchStart }),
            mark('request-start', { startTime: entry.requestStart }),
            mark('time-to-first-byte', { startTime: entry.responseStart - entry.requestStart }),
            mark('time-to-last-byte', { startTime: entry.responseEnd - entry.requestStart }),
          ]);
        } catch (e) {
          console.error(e);
        }

        // $FlowFixMe: Flow doesn't know about PerformanceNavigationTiming.prototype.transferSize
        if (entry.transferSize) {
          payload.current.metrics['document-transferred-bytes'] = entry.transferSize;
        }

        // This is here to retain backward compatibility with old dashboards & ETLs
        payload.current.navigations.push({
          puid: payload.current.pageLoad.uid,
          name: entry.name,
          dns: Math.round(entry.domainLookupEnd - entry.domainLookupStart),
          ttfb: Math.round(entry.responseStart - entry.requestStart),
          download: Math.round(entry.responseEnd - entry.responseStart),
          // $FlowFixMe: Flow doesn't know about PerformanceNavigationTiming.prototype.transferSize
          size: entry.transferSize || 0,
        });
      }
    };

    const handleResourceTiming = (entries: PerformanceResourceTiming[]) => {
      if (haveReachedTTI.current) {
        // We only care about bytes that are a part of page load. We consider page load complete at
        // the point at which we reach TTI + 5s. This probably isn't the best heuristic, but it's
        // an easy one that's mostly accurate.
        return;
      }
      // Resource entries are subject to CORS restrictions unless we're whitelisted via the
      // Timing-Allow-Origin header. Here, we can establish whether or not that's the case.
      // We also only care about PerformanceResourceTimings for byte reasons--aka Resource Timing L2
      // So there's two birds with one stone on this filter.
      entries
        .filter(entry => entry.decodedBodySize > 0)
        .filter(entry => !trackedEntries.has(entry))
        .forEach(entry => {
          trackedEntries.add(entry);
          // Using initiatorType to determine the correct bucket isn't valid, unfortunately.
          // Notably, rel="preload" can force nearly anything into being a 'link' type.
          // As a result, we're forced to attempt to match URLs to buckets.
          if (entry.name.includes(cdnPrefix) && entry.name.endsWith('.js')) {
            safeAdd(payload.current.metrics, 'js-cdn-transferred-bytes', entry.transferSize);
            safeAdd(payload.current.metrics, 'js-cdn-encoded-bytes', entry.encodedBodySize);
            safeAdd(payload.current.metrics, 'js-cdn-decoded-bytes', entry.decodedBodySize);
            // Cache behavior is complicated. Transfer size INCLUDES headers.
          } else if (entry.name.includes(cdnPrefix) && entry.name.endsWith('.css')) {
            safeAdd(payload.current.metrics, 'css-cdn-transferred-bytes', entry.transferSize);
            safeAdd(payload.current.metrics, 'css-cdn-encoded-bytes', entry.encodedBodySize);
            safeAdd(payload.current.metrics, 'css-cdn-decoded-bytes', entry.decodedBodySize);
          } else if (
            entry.name.endsWith('.jpg') ||
            entry.name.endsWith('.jpeg') ||
            entry.name.endsWith('.ico') ||
            entry.name.endsWith('.gif') ||
            entry.name.endsWith('.png') ||
            entry.name.endsWith('.svg') ||
            // Video:
            entry.name.endsWith('.ts') ||
            entry.name.endsWith('.m3u8')
          ) {
            safeAdd(payload.current.metrics, 'media-transferred-bytes', entry.transferSize);
            safeAdd(payload.current.metrics, 'media-request-count', 1);
          } else if (entry.name.includes(domainName) && entry.name.includes('/data/')) {
            safeAdd(payload.current.metrics, 'data-transferred-bytes', entry.transferSize);
            safeAdd(payload.current.metrics, 'data-request-count', 1);
          } else {
            safeAdd(payload.current.metrics, 'other-transferred-bytes', entry.transferSize);
            safeAdd(payload.current.metrics, 'other-request-count', 1);
          }
        });
    };

    const handleLargestContentfulPaint = (entries: LargestContentfulPaint[]) => {
      if (entries.length > 0) {
        // LCP is only concerned with the LAST entry of its type.
        const entry = entries[entries.length - 1];
        // LCP is accurate when the renderTime is available.
        if (entry.renderTime) {
          payload.current.metrics['largest-contentful-paint'] = entry.startTime;
        }
      }
    };

    const handleLayoutShiftTiming = (entries: LayoutShift[]) => {
      // Read about Cumulative Layout Shift Score: https://web.dev/cls/
      entries.forEach(({ hadRecentInput, value }) => {
        if (!hadRecentInput) {
          safeAdd(payload.current.metrics, 'layout-shift-score', value);
        }
      });
    };

    const handleFirstInputTiming = (entries: PerformanceEventTiming[], observer) => {
      if (entries.length > 0) {
        const { processingStart, startTime } = entries[0];
        payload.current.metrics['first-input-delay'] = processingStart - startTime;

        // We log a 'mark' for FID for backwards compatibility with previous dashboards.
        try {
          handlePerformanceEntry([
            mark('first-input-delay', { startTime: processingStart - startTime }),
          ]);
        } catch (e) {
          console.error(e);
        }
        if (observer) {
          observer.disconnect();
        }
      }
    };

    const handleLongTaskTiming = (entries: PerformanceLongTaskTiming[]) => {
      entries.forEach(entry => {
        // Read more about Total Blocking Time: https://web.dev/tbt/
        safeAdd(payload.current.metrics, 'total-blocking-time', entry.duration - 50);
        safeAdd(payload.current.metrics, 'long-task-count', 1);
        payload.current.timings.push([
          [
            'task',
            entry.name,
            ...entry.attribution
              .map(a => a.containerName || a.containerId || a.containerSrc)
              .filter(Boolean),
          ].join(' > '),
          entry.entryType,
          Math.round(entry.startTime),
          Math.round(entry.duration),
        ]);
      });
    };

    const handleElementTiming = (entries: PerformanceElementTiming[]) => {
      entries.forEach(entry => {
        payload.current.timings.push([
          `${entry.identifier} ${entry.name}`,
          'element',
          Math.round(entry.startTime),
          0,
        ]);
      });
    };

    const handlePaintTiming = (entries: PerformanceEntry[]) => {
      // If the document is hidden at any point during the load, let's ignore this data
      // because page load was probably throttled.
      if (performance.getEntriesByName('hidden').length > 0) {
        metricsAreRepresentative.current = false;
      }
      handlePerformanceEntry(entries);
    };

    const cancelers = [
      // Chrome 57, Safari 13, Firefox 58, Edge 12
      [['navigation'], handleNavigationTiming, false],

      // Chrome 43, Safari 11, Firefox 40, Edge 12, IE 10
      // Level 2 (Information about bytes): Chrome 54, Firefox 45, Edge 17
      [['resource'], handleResourceTiming, false],

      // Chrome 25, Safari 11, Firefox 38, Edge 12, IE 10
      // Level 3 (Metadata & backdated marks): Chrome 78
      [['mark', 'measure'], handlePerformanceEntry, false],

      // Chrome 60
      [['paint'], handlePaintTiming, true],

      // The following performance entry types don't participate in the PerformanceTimeline.
      // i.e. they cannot be accessed via Performance.prototype.getEntries
      // As a result, access to their pre-observer entries must come from the buffered flag.

      // Chrome 58
      [['longtask'], handleLongTaskTiming, false],

      // Chrome 77
      [['largest-contentful-paint'], handleLargestContentfulPaint, true],

      // Chrome 77
      [['element'], handleElementTiming, true],

      // Chrome 77
      [['layout-shift'], handleLayoutShiftTiming, true],

      // Chrome 77
      [['first-input'], handleFirstInputTiming, true],
    ].map(([entryTypes, handler, buffered]) => {
      let observer = null;

      // Only Chrome 77+ supports the PerformanceObserver 'buffered' option, so we need to handle
      // reading the buffer manually on idle ourselves. Only alternative would be to polyfill for
      // this, and that seems really excessive and difficult since I'd need to write the thing.
      const idleCallback = requestIdleCallback(() => {
        observer = new PerformanceObserver((list, thiz) =>
          // $FlowFixMe: Flow doesn't like that we're passing an un-asked-for param.
          handler(list.getEntries(), thiz),
        );

        let shouldReadPerformanceBuffer = !buffered;
        try {
          observer.observe(
            entryTypes.length === 1 && buffered
              ? { type: entryTypes[0], buffered }
              : { entryTypes },
          );
        } catch (e) {
          // Browsers that don't support the buffered option throw when you try to use it!
          // Unfortunately there doesn't seem to be a way to feature detect this.
          shouldReadPerformanceBuffer = true;
          try {
            observer.observe({ entryTypes });
          } catch (e2) {
            // Safari interprets this part of the spec:
            // If the resulting entry types sequence is an empty sequence, abort these steps. The
            // user agent SHOULD notify developers when the steps are aborted to notify that
            // registration has been aborted. For example, a console warning might be appropriate.
            // to mean that throwing a TypeError in this state is acceptable. *sigh*.
            // That said... No reason to try and read anything else.
            shouldReadPerformanceBuffer = false;
          }
        }
        if (shouldReadPerformanceBuffer) {
          entryTypes.forEach(entryType => {
            // longtasks aren't buffered by Performance, so we have to exploit an early-initialized
            // PerformanceObserver that writes to a global variable to discover buffered entries,
            // at least until Chrome 81 ships/usage is good enough.
            /* eslint-disable no-restricted-globals,no-underscore-dangle */
            if (entryType === 'longtask' && window.__tti && Array.isArray(window.__tti.e)) {
              handler(window.__tti.e);
              /* eslint-enable no-restricted-globals,no-underscore-dangle */
            } else {
              // $FlowFixMe: Flow doesn't know that we have matching entryType/handler pairs.
              handler(performance.getEntriesByType(entryType));
            }
          });
        }
      });
      return () => {
        cancelIdleCallback(idleCallback);
        if (observer !== null) {
          observer.disconnect();
        }
      };
    });

    requestTTICallback(tti => {
      if (tti !== null && tti !== undefined) {
        haveReachedTTI.current = true;

        // If the document is hidden at any point during the load, let's ignore this data
        // because page load was probably throttled.
        if (performance.getEntriesByName('hidden').length > 0) {
          metricsAreRepresentative.current = false;
        }

        payload.current.metrics['time-to-interactive'] = tti;

        // This is here to retain backward compatibility with old dashboards & ETLs
        handlePerformanceEntry([mark('time-to-interactive', { startTime: tti })]);
      }
    });

    // Native first input support (which is built on PerformanceEventTiming) is only in Chrome 77+.
    // This "polyfill" computes it for all other browsers >= IE9.
    if (typeof PerformanceEventTiming === 'undefined') {
      // Eslint has a false positive on optional chained method calls
      /* eslint-disable no-unused-expressions,no-restricted-globals */
      // $FlowFixMe: Flow doesn't yet support optional chained method calls
      window.perfMetrics?.onFirstInputDelay?.(delay => {
        payload.current.metrics['first-input-delay'] = delay;

        // This is here to retain backward compatibility with old dashboards & ETLs
        handlePerformanceEntry([mark('first-input-delay', { startTime: delay })]);
      });
      /* eslint-enable no-unused-expressions,no-restricted-globals */
    }

    return () => {
      cancelers.forEach(c => c());
    };
  }, [cdnPrefix, domainName]);

  useEffect(() => {
    /* eslint-disable no-restricted-globals */
    try {
      if (window.self !== window.top) {
        metricsAreRepresentative.current = false;
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}

    if (
      // If the real domainName is not the same as our expected domainName the site is probably
      // being proxied and therefore should not be tracked.
      window.location.hostname !== domainName
    ) {
      metricsAreRepresentative.current = false;
    }
    /* eslint-enable no-restricted-globals */

    // If the document is hidden at any point during the load, let's ignore this data because page
    // load was probably throttled. In modern engines that can happen via the CPU or the network.
    // We also do this check at TTI--but for browsers that don't support TTI, the best we can do is
    // prevent us from saving metrics if they were hidden prior to hydration completing--aka now.
    if (performance.getEntriesByName('hidden').length > 0) {
      metricsAreRepresentative.current = false;
    }
  }, [domainName]);

  useEffect(() => {
    /* eslint-disable no-restricted-globals */
    payload.current.pageLoad.user_agent = window.navigator?.userAgent || 'unknown';
    payload.current.pageLoad.connection_type =
      window.navigator?.connection?.effectiveType || 'unknown';
    /* eslint-enable no-restricted-globals */
  }, []);

  // We log on a polyfilled implementation of the Page Lifecycle API. Read about why:
  // https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
  // https://developers.google.com/web/updates/2018/07/page-lifecycle-api#developer-recommendations-for-each-state
  useEffect(() => {
    if (tracked) {
      return undefined;
    }
    tracked = true;
    let submitted = false;
    const listener = event => {
      if (!submitted && event.newState === 'hidden' && metricsAreRepresentative.current) {
        payload.current.metrics['document-dwell-time'] = performance.now();
        Object.entries(payload.current.metrics).forEach(([metric, value]) => {
          // flowlint unclear-type:off
          payload.current.metrics[metric] = threeDigits(((value: any): number));
          // flowlint unclear-type:error
        });

        // $FlowFixMe: Flow declares sendBeacon as conditionally undefined. We've polyfilled it.
        submitted = navigator.sendBeacon('/data/1.0/rum', JSON.stringify(payload.current));
        if (submitted) {
          lifecycle.removeEventListener('statechange', listener);
        }
      }
    };
    lifecycle.addEventListener('statechange', listener);
    return () => {
      tracked = false;
      lifecycle.removeEventListener('statechange', listener);
    };
  }, []);
};

export { useRUM };

/** Page and puid are props to support non-lithium pages that don't have the relevant data in the lithium route context. */
type Props = {|
  +page: ?string,
  +puid: ?string,
|};

const Rum = ({ page, puid }: Props) => {
  useRUM(page !== null && page !== undefined ? page : 'Unknown', puid || '');
  return null;
};

export default React.memo<Props>(Rum);
